import { ReactNode, useCallback, useContext, useEffect } from 'react'

import { Icon, Tooltip } from 'govwell-ui'

import { useLightnessMode } from 'src/hooks/use-lightness-mode'
import { KeyboardKey } from 'src/types'
import { LayoutMode, useSidebarLayoutMode } from 'src/utils/layout'
import { getModifierKey } from 'src/utils/window'

import { Logo, LogoColor } from '../svgs/Logo'

const getSidebarWidth = (isCollapsed: boolean) => {
  return isCollapsed ? 48 : 230
}

type Props = {
  children?: ReactNode
  layoutMode: LayoutMode
}
const SidebarBase = ({ children, layoutMode }: Props) => {
  return (
    <SidebarContext.Provider value={{ layoutMode }}>
      <nav
        className={`${layoutMode === LayoutMode.Topbar ? 'w-full min-w-full' : layoutMode === LayoutMode.SidebarCollapsed ? 'w-[48px] min-w-[48px]' : 'w-sidebar min-w-sidebar'} color-neutral-950 scrollbar-color-neutral-400 bg-layout transition-width isolate flex h-full flex-col overflow-x-hidden overflow-y-auto duration-200 ease-out`}
        style={{
          width:
            layoutMode === LayoutMode.Topbar
              ? '100%'
              : getSidebarWidth(layoutMode === LayoutMode.SidebarCollapsed),
          minWidth:
            layoutMode === LayoutMode.Topbar
              ? '100%'
              : getSidebarWidth(layoutMode === LayoutMode.SidebarCollapsed),
        }}
      >
        {children}
      </nav>
    </SidebarContext.Provider>
  )
}

type SidebarHeaderProps = {
  children?: ReactNode
}
const SidebarHeader = ({ children }: SidebarHeaderProps) => {
  const { layoutMode } = useSidebarContext()
  const { isDarkMode } = useLightnessMode()

  const { isSidebarCollapsed, setIsSidebarCollapsed } = useSidebarLayoutMode()
  const handleToggleCollapse = useCallback(() => {
    setIsSidebarCollapsed(!isSidebarCollapsed)
  }, [isSidebarCollapsed, setIsSidebarCollapsed])

  useEffect(() => {
    const listener = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === KeyboardKey.Backslash) {
        handleToggleCollapse()
      }
    }
    window.addEventListener('keydown', listener)
    return () => window.removeEventListener('keydown', listener)
  }, [handleToggleCollapse, isSidebarCollapsed, setIsSidebarCollapsed])

  const modifierKey = getModifierKey()

  return (
    <header
      className="bg-layout sticky top-0 z-1 flex w-full flex-col gap-2 p-2"
      style={{ minWidth: getSidebarWidth(isSidebarCollapsed) }}
    >
      {layoutMode !== LayoutMode.Topbar && (
        <div
          className={`flex items-center justify-center gap-0.5 ${isSidebarCollapsed ? 'flex-col' : 'flex-row'}`}
        >
          <div className={`py-2 pr-4 ${isSidebarCollapsed ? 'pl-4' : 'pl-2'}`}>
            <Logo
              color={isDarkMode ? LogoColor.White : LogoColor.Black}
              showText={!isSidebarCollapsed}
              width={isSidebarCollapsed ? 26 : undefined}
              height={isSidebarCollapsed ? 19 : undefined}
            />
          </div>
          <Tooltip
            content={`${modifierKey}+\\ ${isSidebarCollapsed ? 'Open' : 'Close'}`}
            side="right"
          >
            <button
              className="flex cursor-pointer items-center justify-center rounded-lg p-2 outline-offset-1 outline-neutral-950 hover:bg-neutral-200 focus-visible:outline dark:text-neutral-100 dark:outline-neutral-100 dark:hover:bg-neutral-600"
              onClick={handleToggleCollapse}
              style={{
                width: isSidebarCollapsed ? '32px' : 'auto',
                minWidth: isSidebarCollapsed ? '32px' : 'auto',
              }}
            >
              <Icon icon={isSidebarCollapsed ? 'FaSidebar' : 'FaSidebarSolid'} />
            </button>
          </Tooltip>
        </div>
      )}
      {children}
    </header>
  )
}

type SidebarContentProps = {
  children?: ReactNode
}
const SidebarContent = ({ children }: SidebarContentProps) => {
  return (
    <div className="scrollbar-color-neutral-400 scrollbar-w-thin mt-1.5 flex flex-1 flex-col gap-[1px] overflow-x-hidden overflow-y-auto px-2 pt-0.5 pb-2">
      {children}
    </div>
  )
}

type SidebarFooterProps = {
  children?: ReactNode
}
const SidebarFooter = ({ children }: SidebarFooterProps) => {
  return (
    <footer className="bg-layout flex flex-col gap-[1px] border-t-1 border-neutral-200 p-2 dark:border-neutral-600">
      {children}
    </footer>
  )
}

type SidebarContextType = {
  layoutMode: LayoutMode
}
const SidebarContext = React.createContext<SidebarContextType>({
  layoutMode: LayoutMode.Topbar,
})
export const useSidebarContext = () => useContext(SidebarContext)

export const SidebarPrimitive = Object.assign(SidebarBase, {
  Header: SidebarHeader,
  Content: SidebarContent,
  Footer: SidebarFooter,
})
